import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Sparkles, X, MessageSquare, Check, UserPlus } from 'lucide-react';
import { matchingApi } from '../../api';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';

const MatchSuggestions = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      const response = await matchingApi.getSuggestions(10);
      setSuggestions(response.data);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (suggestion) => {
    setConnecting(suggestion.id);
    try {
      // API returns flat object - use user_id or id
      const receiverId = suggestion.suggested_user_id || suggestion.user_id || suggestion.id;
      await matchingApi.createConnection({
        receiver_id: receiverId,
        connection_type: suggestion.match_type || 'collaboration',
        message: `Hi! I'd love to connect with you.`
      });
      // Remove from suggestions
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
    } catch (error) {
      console.error('Failed to connect:', error);
    } finally {
      setConnecting(null);
    }
  };

  const handleDismiss = async (suggestion) => {
    try {
      await matchingApi.dismissSuggestion(suggestion.id);
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
    } catch (error) {
      console.error('Failed to dismiss:', error);
    }
  };

  const getMatchTypeLabel = (type) => {
    switch (type) {
      case 'mentor_mentee': return { label: 'Mentor Match', color: 'bg-purple-100 text-purple-700' };
      case 'collaboration': return { label: 'Collaborator', color: 'bg-blue-100 text-blue-700' };
      case 'skill_exchange': return { label: 'Skill Exchange', color: 'bg-orange-100 text-orange-700' };
      case 'cofounder': return { label: 'Co-Founder', color: 'bg-red-100 text-red-700' };
      default: return { label: 'Network', color: 'bg-emerald-100 text-emerald-700' };
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
        <div className="h-48 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-emerald-500" />
          <h3 className="font-semibold text-gray-900">{t('connections.suggested')}</h3>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          {t('connections.suggested_subtitle')}
        </p>
      </div>

      {suggestions.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>{t('connections.no_connections')}</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {suggestions.map((suggestion) => {
            const matchType = getMatchTypeLabel(suggestion.match_type);
            // API returns flat object, not nested suggested_user
            const suggestedUser = suggestion.suggested_user || suggestion;
            
            return (
              <div key={suggestion.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start gap-4">
                  <img
                    src={suggestedUser?.avatar || `https://ui-avatars.com/api/?name=${suggestedUser?.name || 'User'}&background=10b981&color=fff`}
                    alt={suggestedUser?.name}
                    className="w-14 h-14 rounded-full object-cover"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-900">{suggestedUser?.name || 'Unknown'}</h4>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${matchType.color}`}>
                        {matchType.label}
                      </span>
                    </div>
                    
                    {suggestedUser?.bio && (
                      <p className="text-sm text-gray-500 line-clamp-2 mb-2">{suggestedUser.bio}</p>
                    )}
                    
                    {/* Skills */}
                    {suggestedUser?.skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {suggestedUser.skills.slice(0, 3).map((skill, i) => (
                          <span key={i} className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Match Score */}
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 rounded-full"
                          style={{ width: `${suggestion.match_score || suggestedUser?.aura_score || 75}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-emerald-600">
                        {Math.round(suggestion.match_score || suggestedUser?.aura_score || 75)}% match
                      </span>
                    </div>
                    
                    {/* Reasons */}
                    {suggestion.reasons?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {suggestion.reasons.slice(0, 2).map((reason, i) => (
                          <span key={i} className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                            {reason}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Common Skills/Interests */}
                    <div className="flex flex-wrap gap-1">
                      {suggestion.common_skills?.slice(0, 3).map((skill, i) => (
                        <span key={i} className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">
                          {skill}
                        </span>
                      ))}
                      {suggestion.common_interests?.slice(0, 2).map((interest, i) => (
                        <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                          {interest}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleConnect(suggestion)}
                      disabled={connecting === suggestion.id}
                    >
                      {connecting === suggestion.id ? (
                        'Connecting...'
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4 mr-1" />
                          Connect
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDismiss(suggestion)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      <div className="p-4 bg-gray-50 border-t border-gray-100">
        <Button variant="outline" className="w-full" onClick={fetchSuggestions}>
          <Sparkles className="w-4 h-4 mr-2" />
          Refresh Suggestions
        </Button>
      </div>
    </div>
  );
};

export default MatchSuggestions;
